import { ShiftOption } from '@alliehealth/utils/dist/shifts';
import { Box } from '@mui/material';
import momentTz from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';

import { HistoryItemType } from 'types/myRewards';
import { ReduxStore } from 'types/redux';

import { pxToRem } from 'components/theme/typography';

const ITEM_TYPE_TO_NAME: Record<HistoryItemType, string> = {
    DOCUMENTATION_COMPLIANCE: 'Documentation Compliance',
    UNSCHEDULED_TASK_ADDED: 'Unscheduled/PRN Task Added',
    RESIDENT_NOTE_SUBMITTED: 'Resident Note Submitted',
    SHIFT_CHECK_IN_COMPLETE: 'Shift Check-In Complete',
};

type Props = {
    itemType: HistoryItemType;
    name: string;
    shiftId: number;
    points: number;
    datetimeUtc?: string;
    residentName?: string;
    note?: string;
    branchShifts: ShiftOption[];
};

export const PointHistoryItem = (props: Props) => {
    const { itemType, datetimeUtc, name, residentName, points, shiftId, note, branchShifts } = props;

    const { timezone } = useSelector((state: ReduxStore) => state.session);

    const SHIFT_NAME_TO_TEXT = Object.fromEntries(branchShifts.map((shift) => [shift.id, shift.name]));

    let time: string | null = null;
    if (timezone) {
        time = datetimeUtc
            ? momentTz.utc(datetimeUtc).tz(timezone).format('h:mm A')
            : `${SHIFT_NAME_TO_TEXT[shiftId]} Shift`;
    }

    return (
        <Box
            sx={{
                backgroundColor: '#F8F8F9',
                padding: pxToRem(16),
                borderRadius: pxToRem(8),
                border: '1px solid #F4F4F5',
                marginBottom: pxToRem(12),
                display: 'flex',
                flexDirection: 'column',
                '&:last-of-type': {
                    marginBottom: 0,
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: pxToRem(8),
                }}
            >
                <Box
                    sx={{
                        color: '#CB7E63',
                        fontWeight: 700,
                        fontSize: pxToRem(12),
                    }}
                >
                    {ITEM_TYPE_TO_NAME[itemType]}
                </Box>
                <Box
                    sx={{
                        color: '#6F6F79',
                        fontWeight: 400,
                        fontSize: pxToRem(12),
                    }}
                >
                    {time}
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Box>
                    <Box
                        sx={{
                            fontSize: pxToRem(14),
                            fontWeight: 700,
                            color: '#2C2D3B',
                            lineHeight: pxToRem(24),
                        }}
                    >
                        {name}
                    </Box>
                    {residentName && (
                        <Box
                            sx={{
                                fontSize: pxToRem(14),
                                fontWeight: 400,
                                color: '#2C2D3B',
                                lineHeight: pxToRem(24),
                            }}
                        >
                            {residentName}
                        </Box>
                    )}
                </Box>
                <Box
                    sx={{
                        fontSize: pxToRem(14),
                        fontWeight: 700,
                        color: '#006B75',
                        lineHeight: pxToRem(24),
                    }}
                >
                    +{points}
                </Box>
            </Box>
            {note && (
                <Box
                    sx={{
                        backgroundColor: '#EFEFF1',
                        borderLeft: '2px solid #DEDEE0',
                        paddingTop: pxToRem(8),
                        paddingBottom: pxToRem(8),
                        paddingLeft: pxToRem(10),
                        paddingRight: pxToRem(10),
                        fontSize: pxToRem(12),
                        fontWeight: 500,
                        lineHeight: pxToRem(14),
                        color: '#595964',
                        marginTop: pxToRem(8),
                    }}
                >
                    {note}
                </Box>
            )}
        </Box>
    );
};
